/**
 * Spec 200 Fase 6 #3 — applies the persisted theme preference on the
 * document root and keeps it in sync with three event sources:
 *
 *   1. Mount    — read `cuadrilla-web.theme` from localStorage and
 *                 set `<html data-theme="dark|light">` based on the
 *                 resolved value.
 *   2. matchMedia('(prefers-color-scheme: dark)').change
 *               — re-resolve when the user is on 'auto' and flips
 *                 the OS appearance setting.
 *   3. window 'storage' event
 *               — re-read when another tab or the Settings UI
 *                 dispatches a change. Settings.tsx fires a synthetic
 *                 storage event after writing so the same-tab
 *                 provider doesn't have to listen on a custom event.
 *
 * Why apply on `<html>` (not `<body>` or a wrapper):
 *   The CSS variables in `tokens.css` cascade from `:root[data-theme]`,
 *   which is the `<html>` element. Setting it any lower would force a
 *   full duplicate of the dark-mode block scoped to that wrapper.
 *
 * The hook returns the currently-applied theme (light/dark) so
 * components that need to swap art assets — e.g. brand glyphs — can
 * read it. Most UI just relies on the CSS variables.
 */
import { useEffect, useState } from 'react';

import {
  THEME_STORAGE_KEY,
  readThemePreference,
  resolveAppliedTheme,
  type ThemePreference,
} from './preferences';

export interface UseThemeResult {
  /** The user's preference: 'auto' | 'light' | 'dark'. */
  preference: ThemePreference;
  /** What's actually rendered on the document root. */
  applied: 'light' | 'dark';
}

function applyToDocument(applied: 'light' | 'dark'): void {
  if (typeof document === 'undefined') return;
  // Clear data-theme entirely for light to keep the attribute absent
  // when light is the implicit default — keeps the DevTools surface
  // tidy and avoids a needless `data-theme="light"` on every page.
  if (applied === 'dark') {
    document.documentElement.dataset.theme = 'dark';
  } else {
    delete document.documentElement.dataset.theme;
  }
}

export function useTheme(): UseThemeResult {
  const [preference, setPreference] = useState<ThemePreference>(() => readThemePreference());
  const [applied, setApplied] = useState<'light' | 'dark'>(() =>
    resolveAppliedTheme(readThemePreference()),
  );

  // Apply on every preference change. Doing it inside an effect (not
  // during render) guarantees we only mutate the DOM once the React
  // tree has committed.
  useEffect(() => {
    applyToDocument(applied);
  }, [applied]);

  // Listen for `storage` events so cross-tab and same-tab Settings
  // writes refresh this hook. The Settings UI synthesises the event
  // because the native one only fires on OTHER tabs.
  useEffect(() => {
    function onStorage(e: StorageEvent) {
      if (e.key !== null && e.key !== THEME_STORAGE_KEY) return;
      const next = readThemePreference();
      setPreference(next);
      setApplied(resolveAppliedTheme(next));
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Listen for OS appearance flips. Only meaningful while preference
  // is 'auto'; we still keep the listener attached unconditionally so
  // a switch back to 'auto' picks up the right value immediately
  // without a remount cycle.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    function onChange() {
      // Only react when the user is on 'auto' — explicit light/dark
      // should ignore OS changes.
      if (preference === 'auto') {
        setApplied(mq.matches ? 'dark' : 'light');
      }
    }
    // Older Safari only exposes the legacy addListener API.
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', onChange);
      return () => mq.removeEventListener('change', onChange);
    }
    mq.addListener(onChange);
    return () => mq.removeListener(onChange);
  }, [preference]);

  return { preference, applied };
}
